import { useEffect, useRef } from 'react';

import { readIdentityCookie } from './identityCookie';

/**
 * Calls `onChange` when this tab becomes visible and the session-identity cookie
 * (identityCookie.ts) no longer matches the value it had when the tab loaded. That happens when
 * another tab impersonates, switches company, signs in or signs out.
 *
 * The hook reads the cookie once on mount. If the cookie is absent then (the user signed in
 * before the backend began writing it), the hook does nothing for the life of the tab.
 *
 * `onChange` runs on every visible-and-mismatched event, so the caller decides whether to show
 * the warning again.
 */
export function useSessionIdentityChange(onChange: () => void): void {
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const initialValue = readIdentityCookie();

    if (initialValue === null) {
      return;
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      if (readIdentityCookie() !== initialValue) {
        onChangeRef.current();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
